import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSessionComms } from '../../contexts/SessionCommsContext';
import { sessionApi } from '../../services/sessionApi';
import BackButton from '../../components/BackButton';
import SettingsButton from '../../components/SettingsButton';
import SocialButton from '../../components/SocialButton';
import HelpButton from '../../components/HelpButton';
import getButtonStyle from '../../utils/getButtonStyle';
import storageManager from '../../utils/StorageManager';
import { getVirtualSnapshotSignature } from '../../utils/virtualGameSnapshotSync';

const VIRTUAL_IMPOSTER_PRESENCE_KEY = 'virtual_imposter_presence';
const VIRTUAL_IMPOSTER_TTL_HOURS = 2;

function VirtualImposter() {
    const { theme } = useTheme();
    const { emitRealtimeEvent, activeSessionContext, session: syncedSession, virtualGameSyncTick } = useSessionComms();
    const isMobile = window.innerWidth <= 768;

    const [sessionCode, setSessionCode] = useState('');
    const [alias, setAlias] = useState('');
    const [joinedAlias, setJoinedAlias] = useState('');
    const [session, setSession] = useState(null);
    const [imposterState, setImposterState] = useState({ active: false });
    const [numImposters, setNumImposters] = useState(1);
    const [hintsEnabled, setHintsEnabled] = useState(true);
    const [myRole, setMyRole] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const snapshotSignatureRef = useRef('');
    const activeContextCode = String(activeSessionContext?.code || '').trim().toUpperCase();
    const activeContextAlias = String(activeSessionContext?.alias || '').trim();
    const hasActivePartySession = Boolean(activeContextCode);
    const effectiveSessionCode = hasActivePartySession ? activeContextCode : String(sessionCode || '').trim().toUpperCase();
    const effectiveAlias = hasActivePartySession ? activeContextAlias : String(joinedAlias || '').trim();
    const displayedSession = hasActivePartySession && String(syncedSession?.code || '').trim().toUpperCase() === activeContextCode
        ? syncedSession
        : session;

    const persistPresence = (nextSessionCode, nextJoinedAlias) => {
        if (!nextSessionCode || !nextJoinedAlias) return;
        storageManager.saveTemporaryData(
            VIRTUAL_IMPOSTER_PRESENCE_KEY,
            {
                sessionCode: nextSessionCode.trim().toUpperCase(),
                joinedAlias: nextJoinedAlias,
            },
            VIRTUAL_IMPOSTER_TTL_HOURS
        );
    };

    const loadState = async (codeOverride) => {
        const code = (codeOverride || sessionCode).trim().toUpperCase();
        if (!code) return;
        const data = await sessionApi.getGuestSession(code);
        const nextSnapshot = {
            session: data.session || null,
            imposter: data.imposter || { active: false },
        };
        const nextSignature = getVirtualSnapshotSignature(nextSnapshot);
        if (nextSignature === snapshotSignatureRef.current) {
            return;
        }
        setSessionCode(code);
        setSession(nextSnapshot.session);
        setImposterState(nextSnapshot.imposter);
        snapshotSignatureRef.current = nextSignature;
    };

    useEffect(() => {
        if (!effectiveSessionCode || !effectiveAlias) return;
        const interval = setInterval(() => {
            loadState(effectiveSessionCode).catch((requestError) => {
                if (requestError?.status === 404 || requestError?.status === 410) {
                    if (hasActivePartySession) return;
                    setJoinedAlias('');
                    storageManager.removeTemporaryData(VIRTUAL_IMPOSTER_PRESENCE_KEY);
                }
            });
        }, 5000);
        return () => clearInterval(interval);
    }, [effectiveSessionCode, effectiveAlias, hasActivePartySession]);

    useEffect(() => {
        if (!hasActivePartySession) return;
        if (sessionCode !== activeContextCode) {
            setSessionCode(activeContextCode);
        }
        if (joinedAlias !== activeContextAlias) {
            setJoinedAlias(activeContextAlias);
        }
        if (!alias && activeContextAlias) {
            setAlias(activeContextAlias);
        }
        if (syncedSession) {
            setSession(syncedSession);
        }
        loadState(activeContextCode).catch(() => null);
    }, [hasActivePartySession, activeContextCode, activeContextAlias, alias, joinedAlias, sessionCode, syncedSession]);

    useEffect(() => {
        if (hasActivePartySession) return;
        const cachedPresence = storageManager.getTemporaryData(VIRTUAL_IMPOSTER_PRESENCE_KEY);
        if (cachedPresence?.sessionCode && cachedPresence?.joinedAlias) {
            setSessionCode(cachedPresence.sessionCode);
            setJoinedAlias(cachedPresence.joinedAlias);
            loadState(cachedPresence.sessionCode).catch(() => null);
        }
    }, [hasActivePartySession]);

    useEffect(() => {
        if (displayedSession && effectiveAlias) {
            const stillActive = (displayedSession.participants || []).some((participant) => participant.alias === effectiveAlias);
            if (!stillActive) {
                if (hasActivePartySession) return;
                setJoinedAlias('');
                storageManager.removeTemporaryData(VIRTUAL_IMPOSTER_PRESENCE_KEY);
                setError('Session presence expired. Rejoin to continue receiving updates.');
            }
        }
    }, [displayedSession, effectiveAlias, hasActivePartySession]);

    useEffect(() => {
        if (!effectiveSessionCode || !effectiveAlias) return;
        loadState(effectiveSessionCode).catch(() => null);
    }, [virtualGameSyncTick, effectiveSessionCode, effectiveAlias]);

    const createSession = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await sessionApi.createGuestSession({
                hostAlias: alias.trim() || 'Host',
            });
            const code = data.session?.code;
            const joined = data.session?.hostAlias || alias.trim() || 'Host';
            setJoinedAlias(joined);
            await loadState(code);
            emitRealtimeEvent('session-updated', { code, action: 'created', alias: joined }, code).catch(() => null);
            emitRealtimeEvent('messages-updated', { code, action: 'created', alias: joined }, code).catch(() => null);
            persistPresence(code, joined);
        } catch (requestError) {
            setError(requestError.message || 'Failed to create session');
        } finally {
            setLoading(false);
        }
    };

    const joinSession = async () => {
        try {
            setLoading(true);
            setError('');
            const code = sessionCode.trim().toUpperCase();
            const joined = await sessionApi.joinGuestSession(code, { alias: alias.trim() || 'Guest' });
            const finalAlias = joined.participant?.alias || alias.trim() || 'Guest';
            setJoinedAlias(finalAlias);
            await loadState(code);
            emitRealtimeEvent('session-updated', { code, action: 'joined', alias: finalAlias }, code).catch(() => null);
            emitRealtimeEvent('messages-updated', { code, action: 'joined', alias: finalAlias }, code).catch(() => null);
            persistPresence(code, finalAlias);
        } catch (requestError) {
            setError(requestError.message || 'Failed to join session');
        } finally {
            setLoading(false);
        }
    };

    const startRound = async () => {
        try {
            setLoading(true);
            setError('');
            await sessionApi.startVirtualImposter(effectiveSessionCode, {
                startedByAlias: effectiveAlias,
                numImposters,
                hintsEnabled,
            });
            await loadState(effectiveSessionCode);
            emitRealtimeEvent('session-updated', { code: effectiveSessionCode, action: 'imposter-started', alias: effectiveAlias }, effectiveSessionCode).catch(() => null);
            emitRealtimeEvent('messages-updated', { code: effectiveSessionCode, action: 'imposter-started', alias: effectiveAlias }, effectiveSessionCode).catch(() => null);
        } catch (requestError) {
            setError(requestError.message || 'Failed to start round');
        } finally {
            setLoading(false);
        }
    };

    const revealMyRole = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await sessionApi.getVirtualImposterRole(effectiveSessionCode, effectiveAlias);
            setMyRole(data.role?.text || '');
        } catch (requestError) {
            setError(requestError.message || 'Failed to reveal role');
        } finally {
            setLoading(false);
        }
    };

    const revealAll = async () => {
        try {
            setLoading(true);
            await sessionApi.revealVirtualImposter(effectiveSessionCode);
            await loadState(effectiveSessionCode);
            emitRealtimeEvent('session-updated', { code: effectiveSessionCode, action: 'imposter-revealed' }, effectiveSessionCode).catch(() => null);
            emitRealtimeEvent('messages-updated', { code: effectiveSessionCode, action: 'imposter-revealed' }, effectiveSessionCode).catch(() => null);
        } catch (requestError) {
            setError(requestError.message || 'Failed to reveal imposters');
        } finally {
            setLoading(false);
        }
    };

    const endRound = async () => {
        try {
            setLoading(true);
            await sessionApi.endVirtualImposter(effectiveSessionCode);
            setMyRole('');
            await loadState(effectiveSessionCode);
            emitRealtimeEvent('session-updated', { code: effectiveSessionCode, action: 'imposter-ended' }, effectiveSessionCode).catch(() => null);
            emitRealtimeEvent('messages-updated', { code: effectiveSessionCode, action: 'imposter-ended' }, effectiveSessionCode).catch(() => null);
        } catch (requestError) {
            setError(requestError.message || 'Failed to end round');
        } finally {
            setLoading(false);
        }
    };

    const canControlRound = effectiveAlias && displayedSession?.hostAlias && effectiveAlias === displayedSession.hostAlias;

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            padding: '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        }}>
            <BackButton to="/" />
            <HelpButton helpText="If you are already in a party session, this screen auto-uses that party. Start a round, then reveal your role privately." />
            <SettingsButton />
            <SocialButton />

            <div style={{ marginTop: isMobile ? '45px' : '20px', textAlign: 'center' }}>
                <h1 className="page-title">VIRTUAL IMPOSTER</h1>
            </div>

            <div style={{ maxWidth: '760px', width: '100%', margin: '0 auto', display: 'grid', gap: '10px' }}>
                {hasActivePartySession ? (
                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'grid',
                        gap: '8px'
                    }}>
                        <div style={{ color: theme.textSecondary }}>
                            Using active party session <strong>{effectiveSessionCode}</strong>. Members are synced automatically.
                        </div>
                        <button
                            onClick={() => loadState(effectiveSessionCode).catch((requestError) => setError(requestError.message || 'Failed to refresh'))}
                            style={{
                                ...getButtonStyle(theme, { variant: 'secondary', outline: true, padding: '10px' }),
                            }}
                        >
                            Refresh
                        </button>
                    </div>
                ) : (
                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'grid',
                        gap: '8px'
                    }}>
                        <input
                            value={alias}
                            onChange={(event) => setAlias(event.target.value)}
                            placeholder="Your alias"
                            style={{
                                padding: '10px',
                                borderRadius: '8px',
                                border: `2px solid ${theme.secondary}`,
                                backgroundColor: theme.bgMedium,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                            }}
                        />
                        <input
                            value={sessionCode}
                            onChange={(event) => setSessionCode(event.target.value.toUpperCase())}
                            placeholder="Session code"
                            style={{
                                padding: '10px',
                                borderRadius: '8px',
                                border: `2px solid ${theme.secondary}`,
                                backgroundColor: theme.bgMedium,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                            }}
                        />
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                                onClick={createSession}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    minWidth: '160px',
                                    ...getButtonStyle(theme, {
                                        variant: 'accent',
                                        disabled: loading,
                                        padding: '10px'
                                    }),
                                }}
                            >
                                Create Room
                            </button>
                            <button
                                onClick={joinSession}
                                disabled={loading || !sessionCode}
                                style={{
                                    flex: 1,
                                    minWidth: '160px',
                                    ...getButtonStyle(theme, {
                                        variant: 'secondary',
                                        disabled: loading || !sessionCode,
                                        padding: '10px'
                                    }),
                                }}
                            >
                                Join Room
                            </button>
                            <button
                                onClick={() => loadState().catch((requestError) => setError(requestError.message || 'Failed to refresh'))}
                                style={{
                                    ...getButtonStyle(theme, { variant: 'secondary', outline: true, padding: '10px' }),
                                }}
                            >
                                Refresh
                            </button>
                        </div>
                        {displayedSession?.joinUrl && (
                            <div style={{ color: theme.textSecondary, wordBreak: 'break-all' }}>
                                Share Link: {displayedSession.joinUrl}
                            </div>
                        )}
                    </div>
                )}

                {effectiveAlias && (
                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'grid',
                        gap: '8px'
                    }}>
                        <div>Joined as <strong>{effectiveAlias}</strong> {canControlRound ? '(Host)' : ''}</div>
                        <div>Participants: {(displayedSession?.participants || []).map((player) => player.alias).join(', ') || 'None'}</div>

                        {canControlRound && (
                            <div style={{ display: 'grid', gap: '8px' }}>
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span>Imposters</span>
                                    <input
                                        type="number"
                                        min={1}
                                        max={3}
                                        value={numImposters}
                                        onChange={(event) => setNumImposters(Number(event.target.value) || 1)}
                                        style={{
                                            width: '90px',
                                            padding: '6px',
                                            borderRadius: '8px',
                                            border: `2px solid ${theme.secondary}`,
                                            backgroundColor: theme.bgMedium,
                                            color: theme.textPrimary,
                                            fontFamily: 'inherit',
                                        }}
                                    />
                                </label>
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={hintsEnabled}
                                        onChange={(event) => setHintsEnabled(event.target.checked)}
                                    />
                                    <span>Enable hints for imposters</span>
                                </label>
                                <button
                                    onClick={startRound}
                                    disabled={loading || (displayedSession?.participants?.length || 0) < 3}
                                    style={{
                                        ...getButtonStyle(theme, {
                                            variant: 'accent',
                                            disabled: loading || (displayedSession?.participants?.length || 0) < 3,
                                            padding: '10px'
                                        }),
                                    }}
                                >
                                    Start Virtual Round
                                </button>
                            </div>
                        )}

                        <div style={{ color: theme.textSecondary }}>
                            Round: {imposterState.active ? 'Active' : 'Not active'}
                        </div>
                        {imposterState.active && (
                            <div style={{ color: theme.textSecondary }}>
                                {imposterState.numImposters} imposter(s){imposterState.revealed ? ' • Revealed' : ''}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                                onClick={revealMyRole}
                                disabled={!imposterState.active || loading}
                                style={{
                                    ...getButtonStyle(theme, {
                                        variant: 'accent',
                                        disabled: !imposterState.active || loading,
                                        padding: '10px'
                                    }),
                                }}
                            >
                                Reveal My Role
                            </button>
                            {canControlRound && (
                                <>
                                    <button
                                        onClick={revealAll}
                                        disabled={!imposterState.active || loading}
                                        style={{
                                            ...getButtonStyle(theme, {
                                                variant: 'warning',
                                                disabled: !imposterState.active || loading,
                                                padding: '10px'
                                            }),
                                        }}
                                    >
                                        Reveal All
                                    </button>
                                    <button
                                        onClick={endRound}
                                        disabled={!imposterState.active || loading}
                                        style={{
                                            ...getButtonStyle(theme, {
                                                variant: 'danger',
                                                disabled: !imposterState.active || loading,
                                                padding: '10px'
                                            }),
                                        }}
                                    >
                                        End Round
                                    </button>
                                </>
                            )}
                        </div>

                        {myRole && (
                            <div style={{
                                backgroundColor: theme.bgMedium,
                                borderRadius: '8px',
                                border: `1px solid ${theme.secondary}`,
                                padding: '12px',
                                fontWeight: 'bold'
                            }}>
                                {myRole}
                            </div>
                        )}

                        {imposterState.revealed && (
                            <div style={{ color: theme.textSecondary }}>
                                Word: {imposterState.word} • Imposters: {imposterState.imposters.join(', ')}
                            </div>
                        )}
                    </div>
                )}

                {error && <div style={{ color: theme.danger }}>{error}</div>}
            </div>
        </div>
    );
}

export default VirtualImposter;
